import React, { useState, useEffect } from 'react';
import { 
  Check, X, ArrowRight, Zap, DollarSign, Shield, Users, 
  Smartphone, Brain, Lock, TrendingUp, Award, Sparkles,
  ChevronRight, Clock, Building, Code
} from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';

const ComparisonSection = () => {
  const [activeRow, setActiveRow] = useState(null);
  const [animatedItems, setAnimatedItems] = useState([]);

  // Animate items on mount
  useEffect(() => {
    const timer = setInterval(() => {
      setAnimatedItems(prev => {
        if (prev.length < 8) return [...prev, prev.length];
        return prev;
      });
    }, 150);
    return () => clearInterval(timer);
  }, []);

  const comparisonData = [
    {
      category: 'Ownership Model',
      icon: Lock,
      getCare: { text: 'You OWN it forever', highlight: true },
      saas: { text: 'Rent monthly, lose access if you stop paying' },
      savings: 'Save $50k+ over 5 years'
    },
    {
      category: 'Pricing Structure',
      icon: DollarSign,
      getCare: { text: 'One-time build + low hosting', highlight: true },
      saas: { text: 'Per-user fees that grow with your team' },
      savings: 'No per-user fees'
    },
    {
      category: 'Customisation',
      icon: Code,
      getCare: { text: 'Built for YOUR exact workflow', highlight: true },
      saas: { text: 'One-size-fits-all, you adapt to them' },
      savings: '100% tailored'
    },
    {
      category: 'Data Ownership',
      icon: Shield,
      getCare: { text: '100% yours, export anytime', highlight: true },
      saas: { text: 'Trapped in their ecosystem' },
      savings: 'Full control'
    },
    {
      category: 'NDIS Compliance',
      icon: Award,
      getCare: { text: 'Australian-built, audit-ready', highlight: true },
      saas: { text: 'Generic compliance, manual work' },
      savings: 'Audit-ready'
    },
    {
      category: 'Branding',
      icon: Building,
      getCare: { text: 'Fully white-labelled, your brand', highlight: true },
      saas: { text: 'Their logo, their platform feel' },
      savings: 'Your brand'
    },
    {
      category: 'Mobile App',
      icon: Smartphone,
      getCare: { text: 'Branded iOS & Android apps', highlight: true },
      saas: { text: 'Generic app or none at all' },
      savings: 'Included'
    },
    {
      category: 'AI Features',
      icon: Brain,
      getCare: { text: 'Built-in AI compliance & insights', highlight: true },
      saas: { text: 'Basic or expensive add-ons' },
      savings: 'AI-powered'
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-care-teal/5 to-tech-blue/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-red-500/5 to-orange-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-care-teal/10 rounded-full border border-care-teal/20 mb-6">
            <TrendingUp className="text-care-teal mr-2" size={18} />
            <span className="text-sm font-semibold text-care-teal">The Smart Choice for NDIS Providers</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-navy mb-6">
            <span className="bg-gradient-to-r from-care-teal to-tech-blue bg-clip-text text-transparent">Own Your Platform</span>
            {' '}vs{' '}
            <span className="text-red-500 relative">
              Rent Generic SaaS
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                <path d="M2 6 Q50 2 100 6 T198 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" fill="none" strokeDasharray="4 4"/>
              </svg>
            </span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            See why NDIS providers are switching from subscription software to <strong className="text-text-primary">custom ownership</strong>
          </p>
        </div>

        {/* Interactive Comparison */}
        <div className="max-w-6xl mx-auto">
          {/* Headers */}
          <div className="grid grid-cols-12 gap-4 mb-4">
            <div className="col-span-4"></div>
            <div className="col-span-4">
              <div className="bg-gradient-to-r from-care-teal to-tech-blue p-4 rounded-2xl text-center shadow-lg">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Sparkles className="text-white" size={20} />
                  <span className="text-white font-bold text-lg">GetCare 360</span>
                </div>
                <span className="text-white/80 text-sm">Custom Ownership</span>
              </div>
            </div>
            <div className="col-span-4">
              <div className="bg-gradient-to-r from-gray-200 to-gray-300 p-4 rounded-2xl text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-gray-600 font-bold text-lg">Generic SaaS</span>
                </div>
                <span className="text-gray-500 text-sm">Monthly Rental</span>
              </div>
            </div>
          </div>

          {/* Comparison Rows */}
          <div className="space-y-3">
            {comparisonData.map((row, index) => {
              const Icon = row.icon;
              const isActive = activeRow === index;
              const isAnimated = animatedItems.includes(index);
              
              return (
                <div
                  key={index}
                  onMouseEnter={() => setActiveRow(index)}
                  onMouseLeave={() => setActiveRow(null)}
                  className={`grid grid-cols-12 gap-4 items-stretch transition-all duration-500 ${
                    isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                  }`}
                  style={{ transitionDelay: `${index * 50}ms` }}
                >
                  {/* Category */}
                  <div className={`col-span-4 p-4 rounded-xl flex items-center gap-3 transition-all duration-300 ${
                    isActive ? 'bg-primary-navy text-white scale-[1.02]' : 'bg-white border border-border-line'
                  }`}>
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${
                      isActive ? 'bg-white/20' : 'bg-tech-blue/10'
                    }`}>
                      <Icon className={isActive ? 'text-white' : 'text-tech-blue'} size={20} />
                    </div>
                    <div className="flex-1">
                      <span className={`font-semibold ${isActive ? 'text-white' : 'text-text-primary'}`}>
                        {row.category}
                      </span>
                      {isActive && row.savings && (
                        <div className="text-care-teal text-xs font-medium mt-0.5 animate-fadeIn">
                          {row.savings}
                        </div>
                      )}
                    </div>
                    <ChevronRight className={`transition-all duration-300 ${
                      isActive ? 'text-care-teal rotate-90' : 'text-gray-300'
                    }`} size={18} />
                  </div>

                  {/* GetCare Column */}
                  <div className={`col-span-4 p-4 rounded-xl transition-all duration-300 ${
                    isActive 
                      ? 'bg-gradient-to-r from-care-teal/20 to-tech-blue/20 border-2 border-care-teal/50 scale-[1.02]' 
                      : 'bg-care-teal/5 border border-care-teal/20'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        isActive ? 'bg-care-teal scale-110' : 'bg-care-teal/20'
                      }`}>
                        <Check className={isActive ? 'text-white' : 'text-care-teal'} size={16} />
                      </div>
                      <span className={`text-sm transition-colors ${
                        isActive ? 'text-text-primary font-semibold' : 'text-text-secondary'
                      }`}>
                        {row.getCare.text}
                      </span>
                    </div>
                  </div>

                  {/* Generic SaaS Column */}
                  <div className={`col-span-4 p-4 rounded-xl transition-all duration-300 ${
                    isActive 
                      ? 'bg-red-50 border-2 border-red-200' 
                      : 'bg-gray-50 border border-gray-200'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        isActive ? 'bg-red-500' : 'bg-red-100'
                      }`}>
                        <X className={isActive ? 'text-white' : 'text-red-400'} size={16} />
                      </div>
                      <span className={`text-sm transition-colors ${
                        isActive ? 'text-red-700' : 'text-gray-500'
                      }`}>
                        {row.saas.text}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Summary */}
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {/* GetCare Summary */}
            <div className="bg-gradient-to-br from-care-teal/10 to-tech-blue/10 rounded-2xl p-6 border border-care-teal/20 relative overflow-hidden group hover:shadow-xl transition-all duration-300">
              <div className="absolute top-0 right-0 w-32 h-32 bg-care-teal/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-care-teal flex items-center justify-center">
                    <Award className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-text-primary">GetCare 360</h3>
                    <p className="text-sm text-text-secondary">The Smart Investment</p>
                  </div>
                </div>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-sm text-text-secondary">
                    <Check className="text-care-teal" size={14} />
                    One-time build from $18,500
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-secondary">
                    <Check className="text-care-teal" size={14} />
                    Low monthly hosting from $390
                  </li>
                  <li className="flex items-center gap-2 text-sm text-text-secondary">
                    <Check className="text-care-teal" size={14} />
                    Save $50,000+ over 5 years
                  </li>
                </ul>
                <Link to="/features-pricing">
                  <Button className="w-full bg-care-teal hover:bg-care-teal/90 text-white group/btn">
                    View Full Pricing
                    <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={16} />
                  </Button>
                </Link>
              </div>
            </div>

            {/* SaaS Summary */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-200 relative overflow-hidden">
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                    <Clock className="text-red-500" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-700">Generic SaaS</h3>
                    <p className="text-sm text-gray-500">The Expensive Trap</p>
                  </div>
                </div>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-center gap-2 text-sm text-gray-500">
                    <X className="text-red-400" size={14} />
                    $15-200 per user per month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-500">
                    <X className="text-red-400" size={14} />
                    Costs grow as you hire more staff
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-500">
                    <X className="text-red-400" size={14} />
                    50 staff = $60,000+/year
                  </li>
                </ul>
                <div className="bg-red-100 rounded-lg p-3 text-center">
                  <span className="text-red-600 font-semibold text-sm">Stop renting. Start owning.</span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Link to="/roi-calculator">
              <Button className="bg-tech-blue hover:bg-tech-blue/90 text-white px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-all group">
                <Calculator className="mr-2" size={20} />
                Calculate Your Savings
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </section>
  );
};

// Import Calculator icon
const Calculator = ({ className, size }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <line x1="8" y1="6" x2="16" y2="6" />
    <line x1="8" y1="10" x2="8" y2="10.01" />
    <line x1="12" y1="10" x2="12" y2="10.01" />
    <line x1="16" y1="10" x2="16" y2="10.01" />
    <line x1="8" y1="14" x2="8" y2="14.01" />
    <line x1="12" y1="14" x2="12" y2="14.01" />
    <line x1="16" y1="14" x2="16" y2="14.01" />
    <line x1="8" y1="18" x2="8" y2="18.01" />
    <line x1="12" y1="18" x2="12" y2="18.01" />
    <line x1="16" y1="18" x2="16" y2="18.01" />
  </svg>
);

export default ComparisonSection;
